import { useEffect, useState } from 'react';
import { X, Wallet } from '../../lib/icons';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth';
import { caisseImposee } from '../../lib/permissions';
import { useTresorerieVerrou } from '../../lib/tresorerieEtat';
import { ContactCombobox } from '../shared/ContactCombobox';
import { PrintDoc } from '../print/PrintDoc';
import { attribuerNumero } from '../../lib/souches';
import { fetchTiers, fetchFinances, fetchCompteTiers, enregistrerPaiementTiers, resumeImputation, MODES_PAIEMENT } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

export const EncaissementClientDialog = ({ onClose, onDone }) => {
  const { user } = useAuth();
  const { verrou: tresorerieKo, enCours: envoiEnCours, titre: titreVerrou } = useTresorerieVerrou();
  const caisseFixe = caisseImposee(user?.role);
  const [tiers, setTiers] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [compte, setCompte] = useState(null);
  const [f, setF] = useState({ tiersId: '', montant: '', mode: 'Espèces', compteId: caisseFixe ?? 'chq', date: new Date().toISOString().slice(0, 10), notes: '' });
  const [recu, setRecu] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    fetchTiers().then((rows) => setTiers(rows.filter((c) => c.estClient !== false)));
    fetchFinances().then((fin) => {
      setComptes(caisseFixe ? fin.soldes.filter((c) => c.id === caisseFixe) : fin.soldes);
      if (!caisseFixe && !fin.soldes.some((c) => c.id === 'chq')) setF((p) => ({ ...p, compteId: fin.soldes[0]?.id ?? '' }));
    });
  }, [caisseFixe]);

  useEffect(() => {
    if (!f.tiersId) {
      setCompte(null);
      return;
    }
    fetchCompteTiers(f.tiersId).then(setCompte);
  }, [f.tiersId]);

  const solde = compte?.solde ?? 0;
  const aRecevoir = solde < 0 ? -solde : 0; // le client doit à l'huilerie
  const montant = Number(f.montant) || 0;
  const estAvance = montant > aRecevoir;

  const submit = async (e) => {
    e.preventDefault();
    const { error, avance, imputation } = await enregistrerPaiementTiers({ ...f, sens: 'entree' });
    if (error) return toast.error(error);
    toast.success(`Encaissement de ${fmt(montant, 3)} TND enregistré${avance ? ' — avance client, dépasse le solde dû' : ''}`);
    setRecu({
      // Clé de souche propre à l'opération : le n° de pièce du mouvement peut être
      // réattribué côté trésorerie, il ne garantit pas l'unicité du reçu.
      numero: `encaissement:${f.tiersId}:${f.date}:${montant}:${Date.now()}`,
      client: tiers.find((t) => t.id === f.tiersId)?.nom ?? compte?.nom ?? '—',
      avance: avance === true,
      montant,
      soldeAvant: compte ? solde : null,
      soldeApres: compte ? solde + montant : null,
      date: f.date,
      mode: f.mode,
      notes: f.notes,
      imputation,
    });
    onDone?.();
  };

  if (recu)
    return (
      <PrintDoc
        type="Reçu d’encaissement — compte client"
        numero={attribuerNumero('recu_paiement', recu.numero)}
        date={recu.date}
        tiers={recu.client}
        lignes={[
          [
            'Nature de l’opération',
            recu.avance ? 'Avance encaissée du client (dépasse le solde dû)' : 'Encaissement reçu du client',
          ],
          ['Mode de règlement', recu.mode],
          [
            'Solde avant opération',
            recu.soldeAvant === null
              ? 'Indisponible'
              : `${fmt(Math.abs(recu.soldeAvant), 3)} TND ${recu.soldeAvant >= 0 ? '(dû au client)' : '(dû par le client)'}`,
          ],
          ['Imputation sur les bons', resumeImputation(recu.imputation)],
          ['Notes / Justificatif', recu.notes || '—'],
        ]}
        totaux={[
          ['Montant encaissé', `${fmt(recu.montant, 3)} TND`],
          [
            'Nouveau solde du compte',
            recu.soldeApres === null ? 'Indisponible' : `${fmt(Math.abs(recu.soldeApres), 3)} TND`,
          ],
        ]}
        mentions="Reçu à signer par les deux parties. Montants exprimés en dinars tunisiens (TND)."
        onClose={onClose}
      />
    );

  return (
    <div data-testid="encaissement-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={submit} data-testid="encaissement-form" className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 border-t-4 border-t-emerald-600 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <Wallet size={18} className="text-emerald-600" /> Encaisser client
            </h2>
            <p className="text-xs text-slate-500">Argent reçu du client : règlement d’une prestation ou avance versée avant le premier bon.</p>
          </div>
          <button type="button" data-testid="encaissement-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className={label}>Client</label>
            <ContactCombobox contacts={tiers} value={f.tiersId} onChange={(id) => setF((p) => ({ ...p, tiersId: id }))} testid="encaissement-client" />
            {f.tiersId && (
              <p data-testid="encaissement-solde" className="mt-1.5 border-l-2 border-emerald-600 bg-emerald-50 px-2 py-1 font-data text-[11px] text-emerald-800">
                {aRecevoir > 0
                  ? `Dû par le client : ${fmt(aRecevoir, 3)} TND`
                  : solde > 0
                    ? `Compte créditeur : l’huilerie doit ${fmt(solde, 3)} TND à ce tiers`
                    : 'Compte soldé — tout encaissement sera une avance'}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Montant (TND)</label>
              <input data-testid="encaissement-montant" type="number" step="0.001" min="0" value={f.montant} onChange={set('montant')} className={field} />
            </div>
            <div>
              <label className={label}>Date</label>
              <input data-testid="encaissement-date" type="date" value={f.date} onChange={set('date')} className={field} />
            </div>
            <div>
              <label className={label}>Mode de paiement</label>
              <select data-testid="encaissement-mode" value={f.mode} onChange={set('mode')} className={field}>
                {MODES_PAIEMENT.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Caisse / compte crédité</label>
              <select data-testid="encaissement-compte" value={f.compteId} onChange={set('compteId')} disabled={Boolean(caisseFixe)} className={`${field} disabled:bg-slate-100`}>
                {comptes.map((c) => <option key={c.id} value={c.id}>{c.nom} — {fmt(c.solde, 3)} TND</option>)}
              </select>
            </div>
          </div>
          {estAvance && montant > 0 && (
            <span data-testid="encaissement-badge-avance" className="inline-flex h-9 items-center bg-amber-100 px-3 font-data text-xs font-bold uppercase tracking-[0.08em] text-amber-800">
              Avance — dépasse le solde
            </span>
          )}
          <div>
            <label className={label}>Note</label>
            <input data-testid="encaissement-notes" value={f.notes} onChange={set('notes')} placeholder="Ex. : acompte sur trituration, avance avant premier bon" className={field} />
          </div>
        </div>
        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="encaissement-enregistrer"
            disabled={tresorerieKo}
            title={titreVerrou}
            className="h-11 flex-1 bg-emerald-600 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {envoiEnCours
              ? 'Enregistrement en cours…'
              : tresorerieKo
                ? 'Trésorerie bloquée'
                : 'Enregistrer l’encaissement'}
          </button>
          <button type="button" data-testid="encaissement-annuler" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
